import 'react-select-me/lib/ReactSelectMe.css';

import { Plus, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import SelectMe from 'react-select-me';

import type { User } from '../../../../@types/User';
import type { RootState } from '../../../../store/store';
import { getAgentsCommerciaux } from '../../../../utils/api';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { extractUniqueUsersFromFonctionResults } from '../../../../utils/users';
import Badge from '../../../common/Badge';

export interface TransactionAgent {
  userId: number | null;
}

interface AgentsSectionProps {
  hasAgents: boolean;
  setHasAgents: (value: boolean) => void;
  agentCount: number;
  setAgentCount: (count: number) => void;
  agents: TransactionAgent[];
  addAgent: () => void;
  updateAgent: (index: number, userId: number | null) => void;
  removeAgent: (index: number) => void;
}

const AgentsSection: React.FC<AgentsSectionProps> = ({
  hasAgents,
  setHasAgents,
  agentCount,
  setAgentCount,
  agents,
  addAgent,
  updateAgent,
  removeAgent,
}) => {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [agentUsers, setAgentUsers] = useState<User[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Fetch agents commerciaux from the API
  useEffect(() => {
    if (!company?.id || !jwt) {
      return;
    }

    setIsLoadingAgents(true);
    fetch(getAgentsCommerciaux(company.id), JWTHeader(jwt))
      .then((response) => response.json())
      .then((data) => {
        if (data && data.results) {
          const users = extractUniqueUsersFromFonctionResults(
            data.results,
            (fonctionResult) => fonctionResult.company?.id === company.id,
          );
          setAgentUsers(users);
        }
      })
      .catch((error) => {
        console.error('Error fetching agents commerciaux:', error);
      })
      .finally(() => {
        setIsLoadingAgents(false);
      });
  }, [company?.id, jwt]);

  const agentOptions = useMemo(() => [...agentUsers]
    .sort((candidateA, candidateB) => (candidateA.lastname || '').localeCompare(candidateB.lastname || ''))
    .map((user) => ({
      value: user.id,
      label: `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
    })), [agentUsers]);

  const hasDuplicates = (() => {
    const ids = agents.map((a) => a.userId).filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
    return new Set(ids).size !== ids.length;
  })();

  const hasNoAgentsAvailable = !isLoadingAgents && agentUsers.length === 0;

  return (
    <div className="mb-8 border-b border-gray-200 pb-8">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">3 - Avez-vous des agents commerciaux pour la transaction ?</h3>
      <div className="mb-4 flex gap-6">
        <label className="flex cursor-pointer items-center">
          <input type="radio" checked={hasAgents} onChange={() => setHasAgents(true)} className="h-5 w-5 cursor-pointer text-blue-600" />
          <span className="ml-3 text-gray-700">Oui</span>
        </label>
        <label className="flex cursor-pointer items-center">
          <input
            type="radio"
            checked={!hasAgents}
            onChange={() => setHasAgents(false)}
            className="h-5 w-5 cursor-pointer text-blue-600"
          />
          <span className="ml-3 text-gray-700">Non</span>
        </label>
      </div>

      {hasAgents && (
        <div className="mt-6 space-y-4">
          {hasNoAgentsAvailable && (
            <Badge
              mode="warning"
              text={<span><span role="img" aria-label="warning">⚠️</span> Aucun agent commercial n'est enregistré pour votre entreprise dans Déclarations Juridiques.</span>}
              className="mb-4"
            />
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Combien ?</label>
            <input
              type="number"
              min="0"
              value={agentCount}
              onChange={(e) => {
                const count = parseInt(e.target.value || '0', 10) || 0;
                setAgentCount(count);
              }}
              className="w-32 rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {agentCount > 0 && agents.length < agentCount && (
            <button onClick={addAgent} className="flex items-center gap-2 rounded-lg bg-rgpd-blue-light px-4 py-2 text-white transition hover:brightness-110">
              <Plus className="h-4 w-4" />
              Ajouter un agent commercial
            </button>
          )}

          <div className="space-y-4">
            {agents.map((agent, index) => (
              <div key={index} className="rounded-lg bg-gray-50 p-4">
                <div className="mb-3 flex items-start justify-between">
                  <span className="text-sm font-medium text-gray-700">Agent commercial {index + 1}</span>
                  <button onClick={() => removeAgent(index)} className="text-red-500 hover:text-red-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Nom</label>
                  {isLoadingAgents ? (
                    <div className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-gray-500">
                      Chargement des agents commerciaux...
                    </div>
                  ) : (
                    <SelectMe
                      options={agentOptions}
                      value={typeof agent.userId === 'number'
                        ? (agentOptions.find((option) => option.value === agent.userId) || { value: null, label: 'Sélectionner...' })
                        : { value: null, label: 'Sélectionner...' }
                      }
                      onChange={(selectedOption) => {
                        const value = (selectedOption && 'value' in selectedOption) ? selectedOption.value as number : null;
                        if (value != null && agents.some((row, i) => i !== index && row.userId === value)) {
                          return; // prevent duplicate selection
                        }
                        updateAgent(index, value);
                      }}
                      placeholder="Sélectionner..."
                      isClearable
                      listPosition="bottom"
                      listMaxHeight={200}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {hasDuplicates && (
            <Badge
              mode="error"
              text={<span><span role="img" aria-label="error">❌</span> Erreur : Vous avez sélectionné le même agent plusieurs fois.</span>}
              className="mt-4"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AgentsSection;


